/* app.slice.ts */
import FlavourConfig from '@shared/config/flavours';
import Storage from '@storage';
import { IAppState, ISplashState } from '../../types/interfaces/states';
import { IConfig, Url } from '../../types/interfaces/config';
import { RootState } from '../store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Initial state for the app slice
 */
const initialState: IAppState = {
  status: {
    activeDeepLinkHandler: false,
    activeWebView: '',
    anonymous: false,
    description: false,
    deviceInitialized: false,
    login: true,
    network: true,
    splash: {
      // First app load should not display ad after splash
      ads: Storage.get('first_load') === '0',
      display: true,
    },
    visibility: true,
  },
  urls: {
    // These Urls are only used in case the backend fails to respond
    account_link: FlavourConfig.defaultUrl.account,
    activate_link: FlavourConfig.defaultUrl.activate,
    register_link: FlavourConfig.defaultUrl.register,
    omnitrack_link: FlavourConfig.defaultUrl.omnitrack,
    freeOmnitrack_link: FlavourConfig.defaultUrl.freeOmnitrack,
  },
};

/**
 * App slice
 */
const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    disableVisibility: (state) => {
      state.status.visibility = false;
    },
    disableNetwork: (state) => {
      state.status.network = false;
    },
    enableNetwork: (state) => {
      state.status.network = true;
    },
    enableVisibility: (state) => {
      state.status.visibility = true;
    },
    setAnonymous: (state, action: PayloadAction<boolean>) => {
      state.status.anonymous = action.payload;
    },
    setDeeplinkHandler: (state, action: PayloadAction<boolean>) => {
      state.status.activeDeepLinkHandler = action.payload;
    },
    setDescription: (state, action: PayloadAction<boolean>) => {
      state.status.description = action.payload;
    },
    setDeviceInitialized: (state, action: PayloadAction<boolean>) => {
      state.status.deviceInitialized = action.payload;
    },
    setLogin: (state, action: PayloadAction<boolean>) => {
      state.status.login = action.payload;
    },
    setSplash: (state, action: PayloadAction<ISplashState>) => {
      state.status.splash = { ...state.status.splash, ...action.payload };
    },
    setUrls: (state, action: PayloadAction<IConfig[]>) => {
      action.payload?.length &&
        action.payload?.forEach((item) => {
          if (item.name && item.values && typeof item.values === 'string') {
            state.urls[item.name as Url] = item.values;
          }
        });
    },
    setWebview: (state, action: PayloadAction<string>) => {
      state.status.activeWebView = action.payload;
    },
  },
});

const { actions, reducer: appReducer } = appSlice;
export const {
  disableNetwork,
  disableVisibility,
  enableNetwork,
  enableVisibility,
  setAnonymous,
  setDeeplinkHandler,
  setDescription,
  setDeviceInitialized,
  setLogin,
  setSplash,
  setUrls,
  setWebview,
} = actions;
export const appSelector = (state: RootState) => state.app;
export default appReducer;
